import { inferMimeTypeFromFilename } from "../fileUtils";

const DB_NAME = "9amHealthSecureFileCache";
const DB_VERSION = 1;
const STORE_NAME = "encryptedFiles";
const KEY_PREFIX = "9am-file-cache-key-";

/**
 * Configuration for the file cache.
 */
export interface FileCacheConfig {
  /**
   * Optional error handler for logging/reporting errors.
   * Errors are caught and not re-thrown to prevent cache failures
   * from breaking the application.
   */
  onError?: (error: unknown, context: { operation: string }) => void;

  /**
   * Maximum age for cached files in milliseconds.
   * Files older than this are treated as cache misses.
   * Default: 7 days (604800000ms)
   */
  maxAgeMs?: number;

  /**
   * Maximum total cache size per user in bytes.
   * When exceeded, oldest entries are evicted.
   * Default: 100MB (104857600 bytes)
   */
  maxSizeBytes?: number;
}

interface EncryptedCacheEntry {
  id: string; // Format: `${userId}:${fileId}`
  userId: string;
  encryptedData: ArrayBuffer;
  iv: Uint8Array;
  filename: string;
  mimeType?: string;
  cachedAt: number;
}

export interface DecryptedFile {
  blob: Blob;
  filename: string;
  mimeType: string;
  /** Native file URI if available (for direct opening without re-writing) */
  uri?: string;
}

export interface CacheStats {
  count: number;
  totalSize: number;
}

export interface CachedFileInfo {
  filename: string;
  mimeType: string;
  uri?: string;
}

export interface FileCache {
  /**
   * Gets a cached file by ID for the current user.
   * Returns null if not found or decryption fails.
   */
  getCachedFile: (fileId: string, userId: string) => Promise<DecryptedFile | null>;

  /**
   * Gets cached file info (metadata + URI) without reading the file contents.
   * Useful for preview where you only need the URI to open the file.
   * Returns null if not found or expired.
   */
  getCachedFileInfo?: (fileId: string, userId: string) => Promise<CachedFileInfo | null>;

  /**
   * Stores a file in the encrypted cache.
   */
  cacheFile: (
    fileId: string,
    userId: string,
    blob: Blob,
    filename: string,
    mimeType?: string
  ) => Promise<void>;

  /**
   * Removes a specific file from the cache.
   */
  removeCachedFile: (fileId: string, userId: string) => Promise<void>;

  /**
   * Clears all cached files for a specific user and removes their encryption key.
   * Call on logout to ensure no data persists.
   */
  clearUserFileCache: (userId?: string) => Promise<void>;

  /**
   * Clears the entire file cache (all users).
   * Use clearUserFileCache for single-user cleanup.
   */
  clearFileCache: () => Promise<void>;

  /**
   * Gets cache statistics for debugging/monitoring.
   */
  getCacheStats: (userId: string) => Promise<CacheStats>;
}

let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Opens or creates the IndexedDB database.
 */
const openDB = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      dbPromise = null;
      reject(request.error ?? new Error("Failed to open IndexedDB"));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("userId", "userId", { unique: false });
        store.createIndex("cachedAt", "cachedAt", { unique: false });
      }
    };
  });

  return dbPromise;
};

/**
 * Generates a new AES-GCM encryption key.
 */
const generateEncryptionKey = async (): Promise<CryptoKey> => {
  return crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true, // extractable - needed to export/import
    ["encrypt", "decrypt"]
  );
};

/**
 * Exports a CryptoKey to a storable string format.
 */
const exportKey = async (key: CryptoKey): Promise<string> => {
  const exported = await crypto.subtle.exportKey("raw", key);
  return btoa(String.fromCharCode(...new Uint8Array(exported)));
};

/**
 * Imports a key from stored string format.
 */
const importKey = async (keyString: string): Promise<CryptoKey> => {
  const keyData = Uint8Array.from(atob(keyString), (c) => c.charCodeAt(0));
  return crypto.subtle.importKey(
    "raw",
    keyData,
    { name: "AES-GCM", length: 256 },
    false, // not extractable after import
    ["encrypt", "decrypt"]
  );
};

/**
 * Gets or creates the encryption key for a user.
 * Key is stored in localStorage, separate from the encrypted data.
 */
const getOrCreateUserKey = async (userId: string): Promise<CryptoKey> => {
  const storageKey = `${KEY_PREFIX}${userId}`;
  const existingKeyString = localStorage.getItem(storageKey);

  if (existingKeyString) {
    return importKey(existingKeyString);
  }

  // Generate new key for this user
  const newKey = await generateEncryptionKey();
  const keyString = await exportKey(newKey);
  localStorage.setItem(storageKey, keyString);

  return newKey;
};

/**
 * Gets the encryption key for a user (returns null if not found).
 */
const getUserKey = async (userId: string): Promise<CryptoKey | null> => {
  const storageKey = `${KEY_PREFIX}${userId}`;
  const keyString = localStorage.getItem(storageKey);

  if (!keyString) return null;

  try {
    return await importKey(keyString);
  } catch {
    return null;
  }
};

/**
 * Encrypts a blob using AES-GCM.
 */
const encryptBlob = async (
  blob: Blob,
  key: CryptoKey
): Promise<{ encryptedData: ArrayBuffer; iv: Uint8Array }> => {
  const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for GCM
  const plaintext = await blob.arrayBuffer();

  const encryptedData = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    plaintext
  );

  return { encryptedData, iv };
};

/**
 * Decrypts data back to a Blob.
 */
const decryptToBlob = async (
  encryptedData: ArrayBuffer,
  iv: Uint8Array,
  key: CryptoKey,
  mimeType?: string
): Promise<Blob> => {
  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(iv) },
    key,
    encryptedData
  );

  return new Blob([decrypted], { type: mimeType });
};

/**
 * Creates a composite cache key from user ID and file ID.
 */
const createCacheKey = (userId: string, fileId: string): string =>
  `${userId}:${fileId}`;

/**
 * Clears all localStorage keys matching the cache prefix.
 */
const clearAllCacheKeys = (): void => {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(KEY_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
};

export const DEFAULT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
export const DEFAULT_MAX_SIZE_BYTES = 100 * 1024 * 1024; // 100MB

/**
 * Creates a file cache instance with the given configuration.
 */
export function createFileCache(config: FileCacheConfig = {}): FileCache {
  const maxAgeMs = config.maxAgeMs ?? DEFAULT_MAX_AGE_MS;
  const maxSizeBytes = config.maxSizeBytes ?? DEFAULT_MAX_SIZE_BYTES;

  const reportError = (error: unknown, operation: string): void => {
    config.onError?.(error, { operation });
  };

  const getCachedFile = async (
    fileId: string,
    userId: string
  ): Promise<DecryptedFile | null> => {
    if (!userId) return null;

    try {
      const key = await getUserKey(userId);
      if (!key) return null; // No key = can't decrypt = cache miss

      const db = await openDB();
      const cacheKey = createCacheKey(userId, fileId);

      const entry = await new Promise<EncryptedCacheEntry | null>(
        (resolve, reject) => {
          const transaction = db.transaction(STORE_NAME, "readonly");
          const store = transaction.objectStore(STORE_NAME);
          const request = store.get(cacheKey);

          request.onerror = () =>
            reject(request.error ?? new Error("Failed to get cached file"));
          request.onsuccess = () =>
            resolve((request.result as EncryptedCacheEntry | undefined) ?? null);
        }
      );

      if (!entry) return null;

      // Check if entry has expired
      if (Date.now() - entry.cachedAt > maxAgeMs) {
        // Clean up expired entry in background
        removeCachedFile(fileId, userId).catch(() => {});
        return null;
      }

      // Resolve MIME type - handle legacy cached files that may have undefined mimeType
      const resolvedMimeType =
        entry.mimeType || inferMimeTypeFromFilename(entry.filename);

      // Decrypt the data with resolved MIME type
      const blob = await decryptToBlob(
        entry.encryptedData,
        entry.iv,
        key,
        resolvedMimeType
      );

      return {
        blob,
        filename: entry.filename,
        mimeType: resolvedMimeType
      };
    } catch (error) {
      // Decryption failure or other error - treat as cache miss
      reportError(error, "getCachedFile");
      return null;
    }
  };

  /**
   * Evicts oldest entries for a user until total size is under maxSizeBytes.
   */
  const evictOldestEntries = async (
    db: IDBDatabase,
    userId: string,
    currentSize: number
  ): Promise<void> => {
    if (currentSize <= maxSizeBytes) return;

    const entries = await new Promise<EncryptedCacheEntry[]>(
      (resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, "readonly");
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index("userId");
        const request = index.getAll(IDBKeyRange.only(userId));

        request.onerror = () =>
          reject(request.error ?? new Error("Failed to get entries for eviction"));
        request.onsuccess = () => resolve(request.result);
      }
    );

    // Sort by cachedAt ascending (oldest first)
    entries.sort((a, b) => a.cachedAt - b.cachedAt);

    let sizeToFree = currentSize - maxSizeBytes;
    const idsToDelete: string[] = [];

    for (const entry of entries) {
      if (sizeToFree <= 0) break;
      idsToDelete.push(entry.id);
      sizeToFree -= entry.encryptedData.byteLength;
    }

    if (idsToDelete.length === 0) return;

    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      let completed = 0;

      for (const id of idsToDelete) {
        const request = store.delete(id);
        request.onerror = () =>
          reject(request.error ?? new Error("Failed to delete entry during eviction"));
        request.onsuccess = () => {
          completed++;
          if (completed === idsToDelete.length) resolve();
        };
      }
    });
  };

  const cacheFile = async (
    fileId: string,
    userId: string,
    blob: Blob,
    filename: string,
    mimeType?: string
  ): Promise<void> => {
    if (!userId) return;

    try {
      const key = await getOrCreateUserKey(userId);
      const { encryptedData, iv } = await encryptBlob(blob, key);

      const db = await openDB();
      const cacheKey = createCacheKey(userId, fileId);

      // Ensure MIME type is always stored - prefer provided, then blob.type, then infer from filename
      const resolvedMimeType =
        mimeType || blob.type || inferMimeTypeFromFilename(filename);

      await new Promise<void>((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, "readwrite");
        const store = transaction.objectStore(STORE_NAME);

        const entry: EncryptedCacheEntry = {
          id: cacheKey,
          userId,
          encryptedData,
          iv,
          filename,
          mimeType: resolvedMimeType,
          cachedAt: Date.now()
        };

        const request = store.put(entry);

        request.onerror = () =>
          reject(request.error ?? new Error("Failed to cache file"));
        request.onsuccess = () => resolve();
      });

      // Check size and evict if needed (non-blocking)
      const stats = await getCacheStats(userId);
      evictOldestEntries(db, userId, stats.totalSize).catch(() => {});
    } catch (error) {
      // Cache failure is not critical - just log and continue
      reportError(error, "cacheFile");
    }
  };

  const removeCachedFile = async (
    fileId: string,
    userId: string
  ): Promise<void> => {
    if (!userId) return;

    try {
      const db = await openDB();
      const cacheKey = createCacheKey(userId, fileId);

      await new Promise<void>((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, "readwrite");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(cacheKey);

        request.onerror = () =>
          reject(request.error ?? new Error("Failed to remove cached file"));
        request.onsuccess = () => resolve();
      });
    } catch {
      // Ignore errors on delete
    }
  };

  const clearUserFileCache = async (userId?: string): Promise<void> => {
    try {
      if (userId) {
        // Remove only this user's encryption key
        localStorage.removeItem(`${KEY_PREFIX}${userId}`);
      } else {
        // Clear all encryption keys when no userId specified
        clearAllCacheKeys();
      }

      // Clear all encrypted data from IndexedDB
      const db = await openDB();
      await new Promise<void>((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, "readwrite");
        const store = transaction.objectStore(STORE_NAME);

        if (userId) {
          // Delete only this user's entries
          const index = store.index("userId");
          const request = index.openCursor(IDBKeyRange.only(userId));

          request.onerror = () =>
            reject(request.error ?? new Error("Failed to clear user file cache"));
          request.onsuccess = () => {
            const cursor = request.result;
            if (cursor) {
              cursor.delete();
              cursor.continue();
            } else {
              resolve();
            }
          };
        } else {
          // Clear everything if no userId (safety fallback)
          const request = store.clear();
          request.onerror = () =>
            reject(request.error ?? new Error("Failed to clear file cache"));
          request.onsuccess = () => resolve();
        }
      });
    } catch {
      // Best effort cleanup - don't throw on logout
    }
  };

  const clearFileCache = async (): Promise<void> => {
    try {
      // Clear all encryption keys
      clearAllCacheKeys();

      // Clear IndexedDB
      const db = await openDB();
      await new Promise<void>((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, "readwrite");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.clear();

        request.onerror = () =>
          reject(request.error ?? new Error("Failed to clear file cache"));
        request.onsuccess = () => resolve();
      });
    } catch {
      // Best effort cleanup
    }
  };

  const getCacheStats = async (userId: string): Promise<CacheStats> => {
    if (!userId) return { count: 0, totalSize: 0 };

    try {
      const db = await openDB();

      return await new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, "readonly");
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index("userId");
        const request = index.getAll(IDBKeyRange.only(userId));

        request.onerror = () =>
          reject(request.error ?? new Error("Failed to get cache stats"));
        request.onsuccess = () => {
          const entries = request.result as EncryptedCacheEntry[];
          const totalSize = entries.reduce(
            (sum, entry) => sum + entry.encryptedData.byteLength,
            0
          );
          resolve({ count: entries.length, totalSize });
        };
      });
    } catch {
      return { count: 0, totalSize: 0 };
    }
  };

  return {
    getCachedFile,
    cacheFile,
    removeCachedFile,
    clearUserFileCache,
    clearFileCache,
    getCacheStats
  };
}
